import { useApi } from '@/hooks/useApi';
import {
  Shield,
  TrendingUp,
  TrendingDown,
  Minus,
  AlertTriangle,
  Star,
  Activity,
  Loader2,
} from 'lucide-react';

// ─── Types ─────────────────────────────────────────────────────

interface SafetyScore {
  id: string;
  driver_id: string;
  driver_name: string;
  vehicle_name?: string;
  period: string;
  overall_score: number;
  speeding_score: number;
  braking_score: number;
  acceleration_score: number;
  idle_score: number;
  geofence_score: number;
  event_counts: Record<string, number>;
  total_miles: number;
  total_trips: number;
  trend: 'improving' | 'stable' | 'declining';
  rank?: number;
}

interface LeaderboardResponse {
  data: SafetyScore[];
  total: number;
}

interface StatsResponse {
  fleet_avg_score: number;
  best_driver: { driver_id: string; driver_name: string; score: number } | null;
  worst_driver: { driver_id: string; driver_name: string; score: number } | null;
  improving_count: number;
  declining_count: number;
  total_drivers: number;
}

// ─── Mock Data ─────────────────────────────────────────────────

const MOCK_LEADERBOARD: LeaderboardResponse = {
  data: [
    { id: '1', driver_id: 'd1', driver_name: 'Maria Santos', vehicle_name: 'Van-012', period: '2026-W10', overall_score: 94, speeding_score: 98, braking_score: 90, acceleration_score: 95, idle_score: 92, geofence_score: 97, event_counts: { speeding: 0, harsh_braking: 2, harsh_acceleration: 1 }, total_miles: 842, total_trips: 31, trend: 'improving', rank: 1 },
    { id: '2', driver_id: 'd2', driver_name: 'James Okafor', vehicle_name: 'Truck-003', period: '2026-W10', overall_score: 87, speeding_score: 90, braking_score: 84, acceleration_score: 88, idle_score: 86, geofence_score: 90, event_counts: { speeding: 2, harsh_braking: 4, harsh_acceleration: 3 }, total_miles: 1124, total_trips: 28, trend: 'stable', rank: 2 },
    { id: '3', driver_id: 'd3', driver_name: 'Priya Nair', vehicle_name: 'Car-007', period: '2026-W10', overall_score: 81, speeding_score: 85, braking_score: 78, acceleration_score: 82, idle_score: 80, geofence_score: 83, event_counts: { speeding: 3, harsh_braking: 6, harsh_acceleration: 4 }, total_miles: 612, total_trips: 22, trend: 'improving', rank: 3 },
    { id: '4', driver_id: 'd4', driver_name: 'Carlos Rivera', vehicle_name: 'Van-008', period: '2026-W10', overall_score: 73, speeding_score: 78, braking_score: 69, acceleration_score: 75, idle_score: 71, geofence_score: 76, event_counts: { speeding: 5, harsh_braking: 9, harsh_acceleration: 6 }, total_miles: 934, total_trips: 35, trend: 'declining', rank: 4 },
    { id: '5', driver_id: 'd5', driver_name: 'Aisha Williams', vehicle_name: 'Truck-015', period: '2026-W10', overall_score: 62, speeding_score: 65, braking_score: 58, acceleration_score: 64, idle_score: 60, geofence_score: 67, event_counts: { speeding: 8, harsh_braking: 14, harsh_acceleration: 9 }, total_miles: 1203, total_trips: 40, trend: 'declining', rank: 5 },
  ],
  total: 5,
};

const MOCK_STATS: StatsResponse = {
  fleet_avg_score: 79,
  best_driver: { driver_id: 'd1', driver_name: 'Maria Santos', score: 94 },
  worst_driver: { driver_id: 'd5', driver_name: 'Aisha Williams', score: 62 },
  improving_count: 2,
  declining_count: 2,
  total_drivers: 5,
};

// ─── Helpers ───────────────────────────────────────────────────

function scoreColor(score: number): string {
  if (score >= 80) return '#10B981';
  if (score >= 60) return '#EAB308';
  if (score >= 40) return '#F97316';
  return '#EF4444';
}

function scoreBg(score: number): string {
  if (score >= 80) return 'rgba(16,185,129,0.15)';
  if (score >= 60) return 'rgba(234,179,8,0.15)';
  if (score >= 40) return 'rgba(249,115,22,0.15)';
  return 'rgba(239,68,68,0.15)';
}

function TrendIcon({ trend }: { trend: 'improving' | 'stable' | 'declining' }) {
  if (trend === 'improving') return <TrendingUp className="w-4 h-4" style={{ color: '#10B981' }} />;
  if (trend === 'declining') return <TrendingDown className="w-4 h-4" style={{ color: '#EF4444' }} />;
  return <Minus className="w-4 h-4 text-muted-foreground" />;
}

function totalEvents(counts: Record<string, number>): number {
  return Object.values(counts).reduce((sum, n) => sum + n, 0);
}

// ─── Page ──────────────────────────────────────────────────────

export default function SafetyPage() {
  const { data: leaderboardData, isLoading: loadingLeaderboard } = useApi<LeaderboardResponse>('/api/safety/leaderboard');
  const { data: statsData } = useApi<StatsResponse>('/api/safety/stats');

  const lb = leaderboardData ?? MOCK_LEADERBOARD;
  const stats = statsData ?? MOCK_STATS;

  const weeklyEvents = lb.data.reduce((sum, s) => sum + totalEvents(s.event_counts), 0);
  const improvingPct = stats.total_drivers > 0
    ? Math.round((stats.improving_count / stats.total_drivers) * 100)
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg" style={{ background: 'rgba(16,185,129,0.1)' }}>
          <Shield className="w-6 h-6" style={{ color: '#10B981' }} />
        </div>
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--foreground)' }}>Driver Safety</h1>
          <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>Scorecard, leaderboard, and gamification</p>
        </div>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Shield}
          label="Fleet Safety Score"
          value={`${stats.fleet_avg_score}`}
          suffix="/100"
          color={scoreColor(stats.fleet_avg_score)}
        />
        <StatCard
          icon={Star}
          label="Safest Driver"
          value={stats.best_driver?.driver_name ?? '—'}
          sub={stats.best_driver ? `Score: ${stats.best_driver.score}` : undefined}
          color="#10B981"
        />
        <StatCard
          icon={AlertTriangle}
          label="Events This Week"
          value={String(weeklyEvents)}
          color={weeklyEvents > 30 ? '#EF4444' : '#F97316'}
        />
        <StatCard
          icon={Activity}
          label="Improving"
          value={`${improvingPct}%`}
          sub={`${stats.improving_count} of ${stats.total_drivers} drivers`}
          color="#3B82F6"
        />
      </div>

      {/* Leaderboard */}
      <div
        className="rounded-xl overflow-hidden"
        style={{
          background: 'var(--glass-bg)',
          border: '1px solid var(--glass-border)',
          backdropFilter: 'blur(var(--glass-blur))',
        }}
      >
        <div className="px-5 py-4 border-b" style={{ borderColor: 'var(--glass-border)' }}>
          <h2 className="text-base font-semibold" style={{ color: 'var(--foreground)' }}>Driver Leaderboard</h2>
          <p className="text-sm mt-0.5" style={{ color: 'var(--muted-foreground)' }}>Ranked by overall safety score this week</p>
        </div>

        {loadingLeaderboard ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-6 h-6 text-muted-foreground animate-spin" />
          </div>
        ) : lb.data.length === 0 ? (
          <div className="text-center py-16" style={{ color: 'var(--muted-foreground)' }}>
            <Shield className="w-8 h-8 mx-auto mb-2 opacity-40" />
            <p>No safety data yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b" style={{ borderColor: 'var(--glass-border)' }}>
                  {['Rank', 'Driver', 'Score', 'Trend', 'Events', 'Miles', 'Trips'].map(h => (
                    <th
                      key={h}
                      className="text-left text-xs font-medium uppercase tracking-wide px-4 py-3"
                      style={{ color: 'var(--muted-foreground)' }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lb.data.map((s) => {
                  const events = totalEvents(s.event_counts);
                  return (
                    <tr
                      key={s.id}
                      className="border-b transition-colors hover:bg-white/5"
                      style={{ borderColor: 'var(--glass-border)' }}
                    >
                      {/* Rank */}
                      <td className="px-4 py-3">
                        <span
                          className="inline-flex items-center justify-center w-7 h-7 rounded-full text-sm font-bold"
                          style={{
                            background: (s.rank ?? 99) <= 3 ? 'rgba(234,179,8,0.15)' : 'rgba(156,163,175,0.1)',
                            color: (s.rank ?? 99) <= 3 ? '#EAB308' : 'var(--muted-foreground)',
                          }}
                        >
                          {s.rank}
                        </span>
                      </td>

                      {/* Driver */}
                      <td className="px-4 py-3">
                        <p className="text-sm font-medium" style={{ color: 'var(--foreground)' }}>{s.driver_name}</p>
                        {s.vehicle_name && (
                          <p className="text-xs mt-0.5" style={{ color: 'var(--muted-foreground)' }}>{s.vehicle_name}</p>
                        )}
                      </td>

                      {/* Score with bar */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span
                            className="text-sm font-bold tabular-nums"
                            style={{ color: scoreColor(s.overall_score) }}
                          >
                            {s.overall_score}
                          </span>
                          <div className="w-20 h-1.5 rounded-full bg-white/10 overflow-hidden">
                            <div
                              className="h-full rounded-full transition-all"
                              style={{
                                width: `${s.overall_score}%`,
                                background: scoreColor(s.overall_score),
                              }}
                            />
                          </div>
                        </div>
                        <span
                          className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium mt-1"
                          style={{ background: scoreBg(s.overall_score), color: scoreColor(s.overall_score) }}
                        >
                          {s.overall_score >= 80 ? 'Excellent' : s.overall_score >= 60 ? 'Good' : s.overall_score >= 40 ? 'Fair' : 'Poor'}
                        </span>
                      </td>

                      {/* Trend */}
                      <td className="px-4 py-3">
                        <TrendIcon trend={s.trend} />
                      </td>

                      {/* Events */}
                      <td className="px-4 py-3">
                        <span
                          className="text-sm font-mono"
                          style={{ color: events > 10 ? '#EF4444' : events > 5 ? '#F97316' : 'var(--foreground)' }}
                        >
                          {events}
                        </span>
                      </td>

                      {/* Miles */}
                      <td className="px-4 py-3">
                        <span className="text-sm tabular-nums" style={{ color: 'var(--muted-foreground)' }}>
                          {s.total_miles.toLocaleString()}
                        </span>
                      </td>

                      {/* Trips */}
                      <td className="px-4 py-3">
                        <span className="text-sm tabular-nums" style={{ color: 'var(--muted-foreground)' }}>
                          {s.total_trips}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────

function StatCard({
  icon: Icon,
  label,
  value,
  suffix,
  sub,
  color,
}: {
  icon: React.ComponentType<{ className?: string; style?: React.CSSProperties }>;
  label: string;
  value: string;
  suffix?: string;
  sub?: string;
  color: string;
}) {
  return (
    <div
      className="rounded-xl p-5"
      style={{
        background: 'var(--glass-bg)',
        border: '1px solid var(--glass-border)',
        backdropFilter: 'blur(var(--glass-blur))',
      }}
    >
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4" style={{ color }} />
        <span className="text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--muted-foreground)' }}>
          {label}
        </span>
      </div>
      <div className="flex items-baseline gap-1">
        <span className="text-2xl font-bold" style={{ color }}>
          {value}
        </span>
        {suffix && (
          <span className="text-sm" style={{ color: 'var(--muted-foreground)' }}>{suffix}</span>
        )}
      </div>
      {sub && (
        <p className="text-xs mt-1" style={{ color: 'var(--muted-foreground)' }}>{sub}</p>
      )}
    </div>
  );
}
